import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Bell, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface MatchEvent {
  id: string;
  match_id: string;
  event_type: string;
  minute: number;
  player_id: string | null;
  team: string | null;
  notes: string | null;
  created_at: string;
  player?: { name: string } | null;
  match?: {
    team_a_name: string | null;
    team_b_name: string | null;
    team_a: string | null;
    team_b: string | null;
    player_a?: { name: string } | null;
    player_b?: { name: string } | null;
  } | null;
}

const getEventEmoji = (type: string) => {
  const emojis: Record<string, string> = {
    goal: '⚽',
    yellow_card: '🟨',
    red_card: '🟥',
    substitution: '🔄',
    penalty: '⚽🎯',
    own_goal: '🤦‍♂️',
    assist: '👟',
    save: '🧤',
    injury: '🏥',
    var: '📺',
  };
  return emojis[type] || '📢';
};

const getEventColor = (type: string) => {
  const colors: Record<string, string> = {
    goal: 'bg-green-500/20 border-green-500/50 text-green-400',
    yellow_card: 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400',
    red_card: 'bg-red-500/20 border-red-500/50 text-red-400',
    substitution: 'bg-blue-500/20 border-blue-500/50 text-blue-400',
    penalty: 'bg-purple-500/20 border-purple-500/50 text-purple-400',
  };
  return colors[type] || 'bg-muted border-border text-muted-foreground';
};

export function FloatingNotificationCenter() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [events, setEvents] = useState<MatchEvent[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [liveMatchIds, setLiveMatchIds] = useState<string[]>([]);
  const navigate = useNavigate();

  // Fetch live matches
  useEffect(() => {
    const fetchLiveMatches = async () => {
      const { data } = await supabase
        .from('matches')
        .select('id')
        .eq('status', 'live');
      
      if (data) {
        setLiveMatchIds(data.map(m => m.id));
      }
    };

    fetchLiveMatches();
    const interval = setInterval(fetchLiveMatches, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch recent events from all live matches
  useEffect(() => {
    const fetchRecentEvents = async () => {
      if (liveMatchIds.length === 0) return;

      const { data } = await supabase
        .from('match_events')
        .select(`
          *,
          player:players(name),
          match:matches(
            team_a_name, team_b_name, team_a, team_b,
            player_a:players!matches_team_a_fkey(name),
            player_b:players!matches_team_b_fkey(name)
          )
        `)
        .in('match_id', liveMatchIds)
        .order('created_at', { ascending: false })
        .limit(20);

      if (data) {
        setEvents(data as MatchEvent[]);
      }
    };

    fetchRecentEvents();
  }, [liveMatchIds]);

  // Subscribe to real-time events
  useEffect(() => {
    if (liveMatchIds.length === 0) return;

    const channel = supabase
      .channel('all-live-events')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'match_events',
        },
        async (payload) => {
          const newEvent = payload.new as MatchEvent;
          
          if (!liveMatchIds.includes(newEvent.match_id)) return;

          // Fetch additional data
          const { data: eventData } = await supabase
            .from('match_events')
            .select(`
              *,
              player:players(name),
              match:matches(
                team_a_name, team_b_name, team_a, team_b,
                player_a:players!matches_team_a_fkey(name),
                player_b:players!matches_team_b_fkey(name)
              )
            `)
            .eq('id', newEvent.id)
            .single();

          if (eventData) {
            setEvents(prev => [eventData as MatchEvent, ...prev.slice(0, 19)]);
            if (!isOpen) {
              setUnreadCount(prev => prev + 1);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [liveMatchIds, isOpen]);

  const handleOpen = () => {
    setIsOpen(true);
    setUnreadCount(0);
  };

  const getMatchName = (event: MatchEvent) => {
    if (!event.match) return 'Unknown Match';
    const teamA = event.match.team_a_name || event.match.player_a?.name || 'Team A';
    const teamB = event.match.team_b_name || event.match.player_b?.name || 'Team B';
    return `${teamA} vs ${teamB}`;
  };

  const getEventText = (event: MatchEvent) => {
    const playerName = event.player?.name || 'Unknown';
    const texts: Record<string, string> = {
      goal: `${playerName} scores!`,
      yellow_card: `Yellow card for ${playerName}`,
      red_card: `Red card for ${playerName}`,
      substitution: event.notes || 'Substitution',
      penalty: `Penalty! ${playerName}`,
    };
    return texts[event.event_type] || event.notes || event.event_type;
  };

  if (liveMatchIds.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {!isOpen ? (
        <Button
          onClick={handleOpen}
          size="lg"
          className="relative rounded-full h-14 w-14 bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25"
        >
          <Bell className="h-6 w-6" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-6 w-6 p-0 flex items-center justify-center bg-red-500 text-white animate-pulse"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      ) : (
        <div className="bg-card border border-border rounded-xl shadow-2xl w-80 overflow-hidden animate-in slide-in-from-bottom-5">
          {/* Header */}
          <div className="flex items-center justify-between p-3 bg-primary/10 border-b border-border">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              <span className="font-semibold text-sm">Live Events</span>
              <Badge variant="secondary" className="text-xs">
                {liveMatchIds.length} live
              </Badge>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setIsMinimized(!isMinimized)}
              >
                {isMinimized ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setIsOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Events List */}
          {!isMinimized && (
            <ScrollArea className="h-80">
              {events.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No events yet</p>
                  <p className="text-xs">Events will appear here in real-time</p>
                </div>
              ) : (
                <div className="p-2 space-y-2">
                  {events.map((event) => (
                    <div
                      key={event.id}
                      onClick={() => navigate(`/matches/${event.match_id}`)}
                      className={`p-2 rounded-lg border cursor-pointer transition-all hover:scale-[1.02] ${getEventColor(event.event_type)}`}
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-lg">{getEventEmoji(event.event_type)}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">
                            {getMatchName(event)}
                          </p>
                          <p className="text-sm font-semibold">
                            {event.minute}' - {getEventText(event)}
                          </p>
                          <p className="text-xs opacity-70">
                            {formatDistanceToNow(new Date(event.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          )}
        </div>
      )}
    </div>
  );
}
